import { cn } from "../cn";
import { ChartEmptyState } from "./ChartEmptyState";
import { type ChartSegment, chartColorFor } from "./chartTypes";

export interface BreakdownBarListProps {
  readonly data: readonly ChartSegment[];
  /**
   * Formats each row's value, e.g. `"12 dossiers"`. Defaults to the plain
   * number.
   */
  readonly formatValue?: (value: number) => string;
  /** Shows a `1.` `2.` `3.` rank prefix instead of a proportional bar. */
  readonly variant?: "bar" | "ranked";
  /** Caps the number of rows shown. Defaults to 6, matching the report cards. */
  readonly limit?: number;
  readonly className?: string;
}

function defaultFormatValue(value: number): string {
  return value.toLocaleString("nl-NL");
}

/**
 * Ranked breakdown of segments as a list, each row showing its share of the
 * total: a proportional bar (`variant="bar"`, e.g. "herkomst dossiers") or a
 * numbered ranking (`variant="ranked"`, e.g. "meest voorkomende merken").
 * Plain CSS rather than a chart library — a labelled progress bar and a
 * percentage are not worth a dependency's axes/tooltip machinery.
 */
export function BreakdownBarList({
  data,
  formatValue = defaultFormatValue,
  variant = "bar",
  limit = 6,
  className,
}: BreakdownBarListProps) {
  const shown = data.slice(0, limit);
  const total = data.reduce((sum, item) => sum + item.value, 0);
  // Two deliberate denominators: bars scale against the largest shown row so a
  // long tail of small shares stays visible, while the percentage stays a share
  // of the full total so the figure means the same thing on every card.
  const max = Math.max(...shown.map((item) => item.value), 1);

  if (shown.length === 0) {
    return <ChartEmptyState className={className} height={120} />;
  }

  if (variant === "ranked") {
    return (
      <ol className={cn("divide-y divide-border/60", className)}>
        {shown.map((item, index) => (
          <li
            key={item.label}
            className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-center gap-2 py-2.5 text-sm"
          >
            <span className="text-xs text-muted-foreground">{index + 1}</span>
            <span
              className={cn(
                "truncate font-medium",
                item.muted && "text-muted-foreground",
              )}
            >
              {item.label}
            </span>
            <span className="grid grid-cols-[auto_2.75rem] gap-3 text-right tabular-nums">
              <span className="text-foreground">{formatValue(item.value)}</span>
              <span className="text-muted-foreground">
                {Math.round((item.value / Math.max(total, 1)) * 100)}%
              </span>
            </span>
          </li>
        ))}
      </ol>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {shown.map((item, index) => (
        <div key={item.label}>
          <div className="mb-1.5 flex items-center justify-between gap-4 text-xs">
            <span
              className={cn(
                "truncate font-medium",
                item.muted && "text-muted-foreground",
              )}
            >
              {item.label}
            </span>
            <span className="grid shrink-0 grid-cols-[auto_2.5rem] gap-3 tabular-nums">
              <span className="text-foreground">{formatValue(item.value)}</span>
              <span className="text-right text-muted-foreground">
                {Math.round((item.value / Math.max(total, 1)) * 100)}%
              </span>
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full"
              style={{
                width: `${(item.value / max) * 100}%`,
                backgroundColor: chartColorFor(item, index),
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
